import { useEffect, useState } from "react";
import { useApis } from '../../../../api/useApiClient';
import { useNotification } from "../../../../context/NotificationContext";

import { formatPriceWithCommas } from '../../../utils/stringUtils';

import Box from '@mui/material/Box';
import Typography from '@mui/material/Typography';
import Card from '@mui/material/Card';
import CardContent from '@mui/material/CardContent';
import Badge from '@mui/material/Badge';
import Stack from '@mui/material/Stack';

import Table from '@mui/material/Table';
import TableBody from '@mui/material/TableBody';
import TableCell from '@mui/material/TableCell';
import TableContainer from '@mui/material/TableContainer';
import TableHead from '@mui/material/TableHead';
import TableRow from '@mui/material/TableRow';

import Tooltip from '@mui/material/Tooltip';
import Chip from '@mui/material/Chip';

import LinearProgress from '@mui/material/LinearProgress';

import CartIcon from '@mui/icons-material/ShoppingCart';
import AddIcon from '@mui/icons-material/Add';
import EditItemIcon from '@mui/icons-material/Edit';
import EditPricingIcon from '@mui/icons-material/LocalOffer';
import RemoveItemIcon from '@mui/icons-material/Delete';
import InventoryIcon from '@mui/icons-material/Inventory';
import BuildIcon from '@mui/icons-material/Build';

import Dialog from '@mui/material/Dialog';
import DialogTitle from '@mui/material/DialogTitle';
import DialogContent from '@mui/material/DialogContent';
import DialogActions from '@mui/material/DialogActions';
import TextField from '@mui/material/TextField';
import Button from '@mui/material/Button';

import InventoryStockList from "../../../Inventory/Stock/InventoryStockList";
import ItemsList from "../../../Item/ItemsList";
import ItemPicker from '../../../Common/Form/ItemPicker';
import ConfirmationDialog from '../../../Common/ConfirmationDialog';
import { IconButton } from "@mui/material";

// Transformer function to map API data to UI format
function transformWorkOrderItemData(workOrderItem) {
    console.debug("Transforming work order item data:", workOrderItem);
    return {
        id: workOrderItem.id,
        itemId: workOrderItem.itemId,
        quantity: workOrderItem.quantity,
        unitPrice: workOrderItem.unitPrice,
        unitDiscountAmount: workOrderItem.unitDiscountAmount,
        status: workOrderItem.status,
        sourceType: workOrderItem.sourceType,
        sourceWorkOrderItemId: workOrderItem.sourceWorkOrderItemId,
        itemData: workOrderItem.itemData || {},
    };
}

// Transform work order items list API data to UI format
function transformWorkOrderItemsListData(workOrderItemsList) {
    if (!Array.isArray(workOrderItemsList)) {
        console.warn("Expected an array for work order items list, but got:", workOrderItemsList);
        return [];
    }
    console.debug("Transforming work order items list data:", workOrderItemsList);
    const transformedItems = workOrderItemsList.map(transformWorkOrderItemData);
    console.debug("Transformed work order items list data:", transformedItems);
    return transformedItems;
}


export default function WorkOrderItemsList({ workOrder = {}, disabled = false, allowPricingEdit = true }) {

    const { tenantApi } = useApis();
    const { showNotification } = useNotification();

    const [workOrderItems, setWorkOrderItems] = useState([]);
    const [loading, setLoading] = useState(false);
    const [refresh, setRefresh] = useState(0);

    // State for remove item confirmation dialog
    const [removeDialogOpen, setRemoveDialogOpen] = useState(false);
    const [itemToRemove, setItemToRemove] = useState(null);

    // State for edit quantity dialog
    const [editDialogOpen, setEditDialogOpen] = useState(false);
    const [itemToEdit, setItemToEdit] = useState(null);
    const [editFormValues, setEditFormValues] = useState({
        quantity: '',
    });

    // State for edit pricing dialog
    const [pricingDialogOpen, setPricingDialogOpen] = useState(false);
    const [itemToEditPricing, setItemToEditPricing] = useState(null);
    const [pricingFormValues, setPricingFormValues] = useState({
        unitPrice: '',
        unitDiscountAmount: '',
    });

    // Load work order items whenever work order or refresh changes
    useEffect(() => {
        const fetchWorkOrderItems = async () => {
            if (!workOrder.id) {
                setWorkOrderItems([]);
                return;
            }

            setLoading(true);
            try {
                const workOrderItemsRes = await tenantApi(`/tenants/work-orders/${workOrder.id}/items`);
                console.debug("Fetched work order items:", workOrderItemsRes);

                // Transform the data to support the UI
                const transformedItems = transformWorkOrderItemsListData(workOrderItemsRes.data);
                setWorkOrderItems(transformedItems);
            } catch (e) {
                console.error("Failed to fetch work order items:", e);
                if (e.message)
                    showNotification(e.message, "error");
                else
                    showNotification(`Failed to fetch items. Please try again later.`, "error");
            } finally {
                setLoading(false);
            }
        };

        fetchWorkOrderItems();
    }, [workOrder, refresh]);


    // Add item from inventory stock (PRODUCT)
    const handleRetailItemAdd = async (event) => {
        console.debug("Selected inventory stock to add:", event.target);
        const inventoryStockRecord = event.target.item;
        const item = inventoryStockRecord.item;

        console.debug("Inventory Stock Record", inventoryStockRecord);
        console.debug("Item to add:", item);

        await addItemToWorkOrder(item.id, 1);
    };

    // Add service item
    const handleServiceItemAdd = async (event) => {
        console.debug("Selected service item to add:", event.target);
        const item = event.target.item;

        console.debug("Service item to add:", item);

        await addItemToWorkOrder(item.id, 1);
    };

    // Common function to add item to work order
    const addItemToWorkOrder = async (itemId, quantity) => {
        setLoading(true);
        try {
            const workOrderItemRes = await tenantApi(`/tenants/work-orders/${workOrder.id}/items`, {
                method: 'POST',
                headers: { 'Content-Type': 'application/json' },
                body: JSON.stringify({
                    itemId: itemId,
                    quantity: quantity,
                }),
            });

            console.debug("Added work order item response:", workOrderItemRes.data);
            
            // Trigger refresh to reload work order items
            setRefresh((prev) => prev + 1);
            showNotification(`Item added to cart`, "success");
        } catch (e) {
            console.error("Failed to add item:", e);
            if (e.message)
                showNotification(e.message, "error");
            else
                showNotification(`Failed to add item. Please try again later.`, "error");
        } finally {
            setLoading(false);
        }
    };

    // Open confirmation dialog for removing an item
    const handleRemoveItemClick = (workOrderItem) => {
        setItemToRemove(workOrderItem);
        setRemoveDialogOpen(true);
    };

    // Cancel remove item action
    const handleRemoveItemCancel = () => {
        setRemoveDialogOpen(false);
        setItemToRemove(null);
    };

    // Open edit dialog for an item
    const handleEditItemClick = (workOrderItem) => {
        setItemToEdit(workOrderItem);
        setEditFormValues({
            quantity: workOrderItem.quantity?.toString() || '',
        });
        setEditDialogOpen(true);
    };

    // Cancel edit item action
    const handleEditItemCancel = () => {
        setEditDialogOpen(false);
        setItemToEdit(null);
        setEditFormValues({
            quantity: '',
        });
    };

    // Handle form input changes
    const handleEditFormChange = (field) => (event) => {
        setEditFormValues((prev) => ({
            ...prev,
            [field]: event.target.value,
        }));
    };

    // Validate edit form
    const isEditFormValid = () => {
        const quantity = parseFloat(editFormValues.quantity);
        return !isNaN(quantity) && quantity > 0;
    };

    // Confirm edit item action
    const handleEditItemConfirm = async () => {
        if (!itemToEdit || !isEditFormValid()) return;

        const updatedValues = {
            quantity: parseFloat(editFormValues.quantity),
        };

        console.debug('Edit confirmed for item:', itemToEdit.itemData?.name, 'ID:', itemToEdit.id);
        console.debug('Updated values:', updatedValues);

        // API call to update the item
        setLoading(true);
        try {
            await tenantApi(`/tenants/work-orders/${workOrder.id}/items/${itemToEdit.id}`, {
                method: 'PATCH',
                headers: { 'Content-Type': 'application/json' },
                body: JSON.stringify(updatedValues),
            });

            console.debug("Updated work order item:", itemToEdit.id);

            // Trigger refresh to reload work order items
            setRefresh((prev) => prev + 1);
            showNotification(`Item "${itemToEdit.itemData?.name}" updated successfully.`, "success");
        }
        catch (e) {
            console.error("Failed to update work order item:", e);
            if (e.message)
                showNotification(e.message, "error");
            else
                showNotification(`Failed to update item. Please try again later.`, "error");
        } finally {
            setLoading(false);
        }

        // Close dialog and reset state
        setEditDialogOpen(false);
        setItemToEdit(null);
        setEditFormValues({
            quantity: '',
        });
    };

    // ========== Pricing Edit Handlers ==========

    // Open pricing edit dialog for an item
    const handleEditPricingClick = (workOrderItem) => {
        setItemToEditPricing(workOrderItem);
        setPricingFormValues({
            unitPrice: workOrderItem.unitPrice?.toString() || '0',
            unitDiscountAmount: workOrderItem.unitDiscountAmount?.toString() || '0',
        });
        setPricingDialogOpen(true);
    };

    // Cancel pricing edit action
    const handleEditPricingCancel = () => {
        setPricingDialogOpen(false);
        setItemToEditPricing(null);
        setPricingFormValues({
            unitPrice: '',
            unitDiscountAmount: '',
        });
    };

    // Handle pricing form input changes
    const handlePricingFormChange = (field) => (event) => {
        setPricingFormValues((prev) => ({
            ...prev,
            [field]: event.target.value,
        }));
    };

    // Validate pricing form
    const isPricingFormValid = () => {
        const unitPrice = parseFloat(pricingFormValues.unitPrice);
        const unitDiscountAmount = parseFloat(pricingFormValues.unitDiscountAmount);
        return !isNaN(unitPrice) && unitPrice >= 0 && 
               !isNaN(unitDiscountAmount) && unitDiscountAmount >= 0 &&
               unitDiscountAmount <= unitPrice;
    };

    // Confirm pricing edit action
    const handleEditPricingConfirm = async () => {
        if (!itemToEditPricing || !isPricingFormValid()) return;

        const updatedValues = {
            unitPrice: parseFloat(pricingFormValues.unitPrice),
            unitDiscountAmount: parseFloat(pricingFormValues.unitDiscountAmount),
        };

        console.debug('Pricing edit confirmed for item:', itemToEditPricing.itemData?.name, 'ID:', itemToEditPricing.id);
        console.debug('Updated pricing values:', updatedValues);

        // API call to update pricing (separate endpoint)
        setLoading(true);
        try {
            await tenantApi(`/tenants/work-orders/${workOrder.id}/items/${itemToEditPricing.id}/pricing`, {
                method: 'PATCH',
                headers: { 'Content-Type': 'application/json' },
                body: JSON.stringify(updatedValues),
            });

            console.debug("Updated work order item pricing:", itemToEditPricing.id);

            // Trigger refresh to reload work order items
            setRefresh((prev) => prev + 1);
            showNotification(`Pricing updated for "${itemToEditPricing.itemData?.name}".`, "success");
        }
        catch (e) {
            console.error("Failed to update work order item pricing:", e);
            if (e.message)
                showNotification(e.message, "error");
            else
                showNotification(`Failed to update pricing. Please try again later.`, "error");
        } finally {
            setLoading(false);
        }

        // Close dialog and reset state
        setPricingDialogOpen(false);
        setItemToEditPricing(null);
        setPricingFormValues({
            unitPrice: '',
            unitDiscountAmount: '',
        });
    };

    // ========== End Pricing Edit Handlers ==========

    // Confirm remove item action
    async function handleRemoveItemConfirm() {
        if (!itemToRemove) return;

        console.debug('Delete confirmed for item:', itemToRemove.itemData?.name, 'ID:', itemToRemove.id);

        setLoading(true);
        try {
            await tenantApi(`/tenants/work-orders/${workOrder.id}/items/${itemToRemove.id}`, {
                method: 'DELETE',
            });

            console.debug("Removed work order item:", itemToRemove.id);

            // Refresh the list to update work order items
            setRefresh((prev) => prev + 1);
            showNotification(`Item "${itemToRemove.itemData?.name}" removed from the cart.`, "success");
        } catch (e) {
            console.error("Failed to remove work order item:", e);
            if (e.message)
                showNotification(e.message, "error");
            else
                showNotification(`Failed to remove item. Please try again later.`, "error");
        } finally {
            setLoading(false);
        }

        // Close dialog and reset state
        setRemoveDialogOpen(false);
        setItemToRemove(null);
    };

    // Calculate line total for an item (unitPrice - unitDiscount) * quantity
    const calculateLineTotal = (item) => {
        const unitPrice = parseFloat(item.unitPrice || 0);
        const unitDiscount = parseFloat(item.unitDiscountAmount || 0);
        const quantity = parseFloat(item.quantity || 0);
        return (unitPrice - unitDiscount) * quantity;
    };

    // Calculate totals (using unitPrice and unitDiscountAmount from work order item)
    const calculateSubTotal = () => {
        return workOrderItems.reduce((sum, item) => {
            const unitPrice = parseFloat(item.unitPrice || 0);
            const quantity = parseFloat(item.quantity || 0);
            return sum + (unitPrice * quantity);
        }, 0);
    };

    const calculateTotalDiscount = () => {
        return workOrderItems.reduce((sum, item) => {
            const unitDiscount = parseFloat(item.unitDiscountAmount || 0);
            const quantity = parseFloat(item.quantity || 0);
            return sum + (unitDiscount * quantity);
        }, 0);
    };

    const calculateNetTotal = () => {
        return calculateSubTotal() - calculateTotalDiscount();
    };

    // Check if quantity editing is allowed (DRAFT or IN_PROGRESS only)
    const canEditQuantity = ['DRAFT', 'IN_PROGRESS'].includes(workOrder.status);
    
    // Check if pricing editing is allowed (DRAFT or CLOSED, and allowPricingEdit prop)
    const canEditPricing = allowPricingEdit && ['DRAFT', 'CLOSED'].includes(workOrder.status);
    
    // Check if items can be added/removed (DRAFT or IN_PROGRESS only)
    const canModifyItems = ['DRAFT', 'IN_PROGRESS'].includes(workOrder.status);

    return (
        <Card sx={{ height: '100%', display: 'flex', flexDirection: 'column' }}>
            <CardContent sx={{ pb: 2, backgroundColor: '#f0f0f0' }}>
                <Box display="flex" justifyContent="space-between" alignItems="center">
                    <Typography variant="h6" display="flex" alignItems="center">
                        <CartIcon sx={{ mr: 1 }} />
                        Cart
                        <Badge badgeContent={workOrderItems.length} color="primary" sx={{ ml: 2 }}>
                            <span></span>
                        </Badge>
                    </Typography>
                    <Stack direction="row" spacing={1}>
                        {/* Add Retail Item from Inventory */}
                        <ItemPicker
                            component="Button"
                            label="Add Product"
                            variant="contained"
                            size="small"
                            icon={<InventoryIcon />}
                            ListView={InventoryStockList}
                            filters={{
                                locationId: workOrder?.location?.id,
                            }}
                            columns={[
                                'itemName',
                                'itemCode',
                                'availableQuantity',
                                'itemUnitPrice',
                            ]}
                            onChange={handleRetailItemAdd}
                            fullWidth={false}
                            disabled={disabled || loading || !canModifyItems}
                        />
                        {/* Add Service Item */}
                        <ItemPicker
                            component="Button"
                            label="Add Service"
                            variant="outlined"
                            size="small"
                            icon={<BuildIcon />}
                            ListView={ItemsList}
                            filters={{
                                type: 'SERVICE',
                                isActive: true,
                            }}
                            columns={[
                                'name',
                                'code',
                                'unitPrice',
                            ]}
                            onChange={handleServiceItemAdd}
                            fullWidth={false}
                            disabled={disabled || loading || !canModifyItems}
                        />
                    </Stack>
                </Box>
            </CardContent>
            {/* Display work order items */}
            <Box sx={{ flexGrow: 1, overflow: 'auto', px: 2 }}>
                <TableContainer>
                    <Table size="small">
                        <TableHead>
                            <TableRow>
                                <TableCell>#</TableCell>
                                <TableCell>Item</TableCell>
                                <TableCell align="right">Unit Price</TableCell>
                                <TableCell align="right">Discount</TableCell>
                                <TableCell align="right">Qty</TableCell>
                                <TableCell align="right">Total</TableCell>
                                <TableCell align="right">Actions</TableCell>
                            </TableRow>
                        </TableHead>
                        <TableBody>
                            {workOrderItems.length === 0 ? (
                                <TableRow>
                                    <TableCell colSpan={7}>
                                        <Box textAlign="center" py={4}>
                                            <CartIcon sx={{ fontSize: 48, color: 'action.disabled', mb: 2 }} />
                                            <Typography variant="body2" color="textSecondary" mb={2}>
                                                Cart is empty
                                            </Typography>
                                        </Box>
                                    </TableCell>
                                </TableRow>
                            ) : (
                                <>
                                    {workOrderItems.map((workOrderItem, index) => (
                                        <TableRow key={workOrderItem.id} hover>

                                            {/* Item number */}
                                            <TableCell>{index + 1}</TableCell>
                                            
                                            {/* Item name with type chip */}
                                            <TableCell
                                                sx={{
                                                    maxWidth: 200,
                                                    overflow: 'hidden',
                                                    textOverflow: 'ellipsis',
                                                    whiteSpace: 'nowrap',
                                                }}
                                            >
                                                <Box display="flex" alignItems="center" gap={1}>
                                                    <Tooltip title={workOrderItem.itemData?.name} arrow>
                                                        <Typography variant="body2" noWrap>{workOrderItem.itemData?.name}</Typography>
                                                    </Tooltip>
                                                    <Chip 
                                                        label={workOrderItem.itemData?.type === 'SERVICE' ? 'Service' : 'Product'} 
                                                        size="small" 
                                                        variant="outlined"
                                                        color={workOrderItem.itemData?.type === 'SERVICE' ? 'info' : 'default'}
                                                    />
                                                </Box>
                                            </TableCell>

                                            {/* Unit price */}
                                            <TableCell align="right">
                                                <Typography variant="body2">
                                                    {formatPriceWithCommas(workOrderItem.unitPrice || 0)}/{workOrderItem.itemData?.uom}
                                                </Typography>
                                            </TableCell>

                                            {/* Unit discount */}
                                            <TableCell align="right">
                                                <Typography variant="body2" color={parseFloat(workOrderItem.unitDiscountAmount) > 0 ? 'error' : 'textSecondary'}>
                                                    {parseFloat(workOrderItem.unitDiscountAmount) > 0 
                                                        ? `-${formatPriceWithCommas(workOrderItem.unitDiscountAmount)}`
                                                        : '-'
                                                    }
                                                </Typography>
                                            </TableCell>

                                            {/* Quantity */}
                                            <TableCell align="right">
                                                <Typography variant="body2">{`${workOrderItem.quantity} ${workOrderItem.itemData?.uom || ''}`}</Typography>
                                            </TableCell>

                                            {/* Line total (after discount) */}
                                            <TableCell align="right">
                                                <Typography variant="body2" fontWeight="bold">
                                                    {formatPriceWithCommas(calculateLineTotal(workOrderItem).toFixed(2))}
                                                </Typography>
                                            </TableCell>

                                            <TableCell align="right">
                                                {/* Edit quantity button */}
                                                <Tooltip title="Edit quantity" arrow>
                                                    <span>
                                                        <IconButton
                                                            size="small"
                                                            onClick={() => handleEditItemClick(workOrderItem)}
                                                            disabled={disabled || loading || !canEditQuantity}
                                                        >
                                                            <EditItemIcon />
                                                        </IconButton>
                                                    </span>
                                                </Tooltip>

                                                {/* Edit pricing button */}
                                                {allowPricingEdit && (
                                                    <Tooltip title="Edit pricing" arrow>
                                                        <span>
                                                            <IconButton
                                                                size="small"
                                                                onClick={() => handleEditPricingClick(workOrderItem)}
                                                                disabled={disabled || loading || !canEditPricing}
                                                                color="primary"
                                                            >
                                                                <EditPricingIcon sx={{ fontSize: 18 }} />
                                                            </IconButton>
                                                        </span>
                                                    </Tooltip>
                                                )}

                                                {/* Remove item button */}
                                                <Tooltip title="Remove item" arrow>
                                                    <span>
                                                        <IconButton
                                                            size="small"
                                                            onClick={() => handleRemoveItemClick(workOrderItem)}
                                                            disabled={disabled || loading || !canModifyItems}
                                                        >
                                                            <RemoveItemIcon color="error" />
                                                        </IconButton>
                                                    </span>
                                                </Tooltip>
                                            </TableCell>
                                        </TableRow>
                                    ))}
                                    
                                    {/* Totals section */}
                                    {/* Sub-total row */}
                                    <TableRow sx={{ backgroundColor: '#fafafa' }}>
                                        <TableCell></TableCell>
                                        <TableCell colSpan={4}>
                                            <Typography variant="body2">Subtotal</Typography>
                                        </TableCell>
                                        <TableCell align="right">
                                            <Typography variant="body2">
                                                {formatPriceWithCommas(calculateSubTotal().toFixed(2))}
                                            </Typography>
                                        </TableCell>
                                        <TableCell></TableCell>
                                    </TableRow>
                                    
                                    {/* Discount row (only show if there's discount) */}
                                    {calculateTotalDiscount() > 0 && (
                                        <TableRow sx={{ backgroundColor: '#fafafa' }}>
                                            <TableCell></TableCell>
                                            <TableCell colSpan={4}>
                                                <Typography variant="body2" color="error">Total Discount</Typography>
                                            </TableCell>
                                            <TableCell align="right">
                                                <Typography variant="body2" color="error">
                                                    -{formatPriceWithCommas(calculateTotalDiscount().toFixed(2))}
                                                </Typography>
                                            </TableCell>
                                            <TableCell></TableCell>
                                        </TableRow>
                                    )}
                                    
                                    {/* Net total row */}
                                    <TableRow sx={{ backgroundColor: '#f0f0f0' }}>
                                        <TableCell></TableCell>
                                        <TableCell colSpan={4}>
                                            <Typography variant="subtitle1" fontWeight="bold">Net Total</Typography>
                                        </TableCell>
                                        <TableCell align="right">
                                            <Typography variant="subtitle1" fontWeight="bold">
                                                {formatPriceWithCommas(calculateNetTotal().toFixed(2))}
                                            </Typography>
                                        </TableCell>
                                        <TableCell></TableCell>
                                    </TableRow>
                                </>
                            )}
                        </TableBody>
                    </Table>
                    <LinearProgress sx={{
                        display: loading ? 'block' : 'none'
                    }} />
                </TableContainer>

            </Box>

            {/* Remove Item Confirmation Dialog */}
            <ConfirmationDialog
                open={removeDialogOpen}
                title="Remove Item"
                message={`Are you sure you want to remove "${itemToRemove?.itemData?.name}" from the cart?`}
                confirmText="Yes, Remove"
                cancelText="No, Keep It"
                confirmColor="error"
                onConfirm={handleRemoveItemConfirm}
                onCancel={handleRemoveItemCancel}
            />

            {/* Edit Item Dialog (Quantity) */}
            <Dialog open={editDialogOpen} onClose={handleEditItemCancel} maxWidth="sm" fullWidth>
                <DialogTitle>Edit Quantity - {itemToEdit?.itemData?.name}</DialogTitle>
                <DialogContent>
                    <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2, pt: 1 }}>
                        <TextField
                            label="Quantity"
                            type="number"
                            value={editFormValues.quantity}
                            onChange={handleEditFormChange('quantity')}
                            onWheel={(e) => e.target.blur()}
                            required
                            fullWidth
                            inputProps={{
                                step: '0.001',
                                min: '0.001',
                            }}
                            helperText={`Unit: ${itemToEdit?.itemData?.uom || ''}`}
                        />
                    </Box>
                </DialogContent>
                <DialogActions>
                    <Button onClick={handleEditItemCancel}>Cancel</Button>
                    <Button
                        onClick={handleEditItemConfirm}
                        variant="contained"
                        disabled={!isEditFormValid()}
                    >
                        Update
                    </Button>
                </DialogActions>
            </Dialog>

            {/* Edit Pricing Dialog */}
            <Dialog open={pricingDialogOpen} onClose={handleEditPricingCancel} maxWidth="sm" fullWidth>
                <DialogTitle>Edit Pricing - {itemToEditPricing?.itemData?.name}</DialogTitle>
                <DialogContent>
                    <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2, pt: 1 }}>
                        <TextField
                            label="Unit Price"
                            type="number"
                            value={pricingFormValues.unitPrice}
                            onChange={handlePricingFormChange('unitPrice')}
                            onWheel={(e) => e.target.blur()}
                            required
                            fullWidth
                            inputProps={{
                                step: '0.01',
                                min: '0',
                            }}
                            helperText={`Original price: ${formatPriceWithCommas(itemToEditPricing?.itemData?.unitPrice || 0)}`}
                        />
                        <TextField
                            label="Unit Discount"
                            type="number"
                            value={pricingFormValues.unitDiscountAmount}
                            onChange={handlePricingFormChange('unitDiscountAmount')}
                            onWheel={(e) => e.target.blur()}
                            fullWidth
                            inputProps={{
                                step: '0.01',
                                min: '0',
                            }}
                            error={parseFloat(pricingFormValues.unitDiscountAmount) > parseFloat(pricingFormValues.unitPrice)}
                            helperText={
                                parseFloat(pricingFormValues.unitDiscountAmount) > parseFloat(pricingFormValues.unitPrice)
                                    ? 'Discount cannot exceed unit price'
                                    : 'Per unit discount amount'
                            }
                        />
                        {/* Preview */}
                        <Box sx={{ mt: 1, p: 2, backgroundColor: '#f5f5f5', borderRadius: 1 }}>
                            <Typography variant="body2" color="textSecondary">
                                Line Total Preview (Qty: {itemToEditPricing?.quantity})
                            </Typography>
                            <Typography variant="h6">
                                {formatPriceWithCommas(
                                    (
                                        (parseFloat(pricingFormValues.unitPrice) || 0) - 
                                        (parseFloat(pricingFormValues.unitDiscountAmount) || 0)
                                    ) * (parseFloat(itemToEditPricing?.quantity) || 0)
                                )}
                            </Typography>
                        </Box>
                    </Box>
                </DialogContent>
                <DialogActions>
                    <Button onClick={handleEditPricingCancel}>Cancel</Button>
                    <Button
                        onClick={handleEditPricingConfirm}
                        variant="contained"
                        disabled={!isPricingFormValid()}
                    >
                        Update Pricing
                    </Button>
                </DialogActions>
            </Dialog>
        </Card>
    );
}
